/**
 * Telemetry data processing utilities
 * Handles filtering, interpolation, and metric calculations for telemetry data
 */

/**
 * Filter telemetry data by time range
 * @param {Array} locations - Telemetry location data
 * @param {number} startTime - Start time in seconds (video time)
 * @param {number} endTime - End time in seconds (video time)
 * @param {number} sessionStartTimestamp - Session start timestamp for conversion
 * @returns {Array} Filtered telemetry data
 */
export function filterTelemetryByTimeRange(locations, startTime, endTime, sessionStartTimestamp) {
  if (!locations || locations.length === 0) return [];
  
  const startTimestamp = sessionStartTimestamp + (startTime * 1000);
  const endTimestamp = sessionStartTimestamp + (endTime * 1000);
  
  return locations.filter(location => 
    location.timestamp >= startTimestamp && 
    location.timestamp <= endTimestamp
  );
}

/**
 * Filter telemetry data by lap number
 * @param {Array} locations - Telemetry location data
 * @param {number} lapNumber - Lap number to filter by
 * @param {Array} lapData - Video session lap data for time range fallback
 * @param {number} sessionStartTimestamp - Session start timestamp
 * @returns {Array} Filtered telemetry data for specific lap
 */
export function filterTelemetryByLap(locations, lapNumber, lapData = [], sessionStartTimestamp = null) {
  if (!locations || locations.length === 0) return [];
  
  // First, try to filter by lapNumber if it exists in the telemetry data
  const lapNumberFiltered = locations.filter(location => location.lapNumber === lapNumber);
  if (lapNumberFiltered.length > 0) {
    return lapNumberFiltered;
  }
  
  // Fallback: Filter by time range if we have lap data and session start time
  if (lapData.length > 0 && sessionStartTimestamp) {
    const targetLap = lapData.find(lap => lap.number === lapNumber);
    if (targetLap) {
      const startTime = targetLap.startTime;
      const endTime = targetLap.endTime || targetLap.startTime + targetLap.duration;
      return filterTelemetryByTimeRange(locations, startTime, endTime, sessionStartTimestamp);
    }
  }
  
  // If no specific lap found and it's lap 0 (Out Lap), return some initial data points
  if (lapNumber === 0) {
    // Return first 50 data points as a reasonable sample for Out Lap
    return locations.slice(0, Math.min(50, locations.length));
  }
  
  return [];
}

/**
 * Get telemetry data point closest to specific time
 * @param {Array} locations - Telemetry location data
 * @param {number} targetTime - Target time in seconds (video time)
 * @param {number} sessionStartTimestamp - Session start timestamp
 * @returns {Object|null} Closest telemetry data point
 */
export function getTelemetryAtTime(locations, targetTime, sessionStartTimestamp) {
  if (!locations || locations.length === 0) return null;
  
  const targetTimestamp = sessionStartTimestamp + (targetTime * 1000);
  
  let closest = null;
  let minDiff = Infinity;
  
  for (const location of locations) {
    const diff = Math.abs(location.timestamp - targetTimestamp);
    if (diff < minDiff) {
      minDiff = diff;
      closest = location;
    }
  }
  
  return closest;
}

/**
 * Interpolate telemetry value between two points
 * @param {Object} point1 - First telemetry point
 * @param {Object} point2 - Second telemetry point
 * @param {number} targetTime - Target time for interpolation
 * @param {string} metric - Metric to interpolate (e.g., 'speed', 'accelerometerX')
 * @returns {number} Interpolated value
 */
export function interpolateTelemetryValue(point1, point2, targetTime, metric) {
  if (!point1 || !point2 || point1[metric] === undefined || point2[metric] === undefined) {
    return point1?.[metric] || point2?.[metric] || 0;
  }
  
  const timeDiff = point2.timestamp - point1.timestamp;
  if (timeDiff === 0) return point1[metric];
  
  const timeRatio = (targetTime - point1.timestamp) / timeDiff;
  const valueDiff = point2[metric] - point1[metric];
  
  return point1[metric] + (valueDiff * timeRatio);
}

/**
 * Calculate speed in different units
 * @param {number} speedMps - Speed in meters per second
 * @param {string} unit - Target unit ('mph', 'kph', 'mps')
 * @returns {number} Speed in requested unit
 */
export function convertSpeed(speedMps, unit = 'mph') {
  if (!speedMps || isNaN(speedMps)) return 0;
  
  switch (unit.toLowerCase()) {
    case 'mph':
      return speedMps * 2.23694; // m/s to mph
    case 'kph':
    case 'kmh':
      return speedMps * 3.6; // m/s to km/h
    case 'mps':
    case 'm/s':
    default:
      return speedMps;
  }
}

/**
 * Calculate G-forces from accelerometer data
 * @param {Object} telemetryPoint - Telemetry data point with accelerometer data
 * @returns {Object} G-force values { lateral, longitudinal, vertical }
 */
export function calculateGForces(telemetryPoint) {
  if (!telemetryPoint) return { lateral: 0, longitudinal: 0, vertical: 0 };
  
  const { accelerometerX = 0, accelerometerY = 0, accelerometerZ = 0 } = telemetryPoint;
  
  // Convert m/s² to G (1G = 9.80665 m/s²)
  const gConstant = 9.80665;
  
  return {
    lateral: accelerometerX / gConstant,
    longitudinal: accelerometerY / gConstant,
    vertical: accelerometerZ / gConstant
  };
}

/**
 * Calculate distance between two GPS points
 * @param {number} lat1 - Latitude of first point
 * @param {number} lon1 - Longitude of first point
 * @param {number} lat2 - Latitude of second point
 * @param {number} lon2 - Longitude of second point
 * @returns {number} Distance in meters
 */
export function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371000; // Earth's radius in meters
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  
  return R * c; // Distance in meters
}

/**
 * Convert degrees to radians
 * @param {number} degrees - Degrees to convert
 * @returns {number} Radians
 */
function toRadians(degrees) {
  return degrees * (Math.PI / 180);
}

/**
 * Calculate cumulative distance for telemetry data
 * This will be replaced once distance is pre-computed in telemetry export
 * @param {Array} locations - Telemetry location data
 * @returns {Array} Locations with cumulative distance added
 */
export function calculateCumulativeDistance(locations) {
  if (!locations || locations.length === 0) return [];
  
  let cumulativeDistance = 0;
  const result = [{ ...locations[0], cumulativeDistance: 0 }];
  
  for (let i = 1; i < locations.length; i++) {
    const prev = locations[i - 1];
    const current = locations[i];
    
    const segmentDistance = calculateDistance(
      prev.latitude, prev.longitude,
      current.latitude, current.longitude
    );
    
    cumulativeDistance += segmentDistance;
    
    result.push({
      ...current,
      cumulativeDistance,
      segmentDistance
    });
  }
  
  return result;
}

/**
 * Sample telemetry data at regular intervals
 * @param {Array} locations - Telemetry location data
 * @param {number} intervalMs - Sampling interval in milliseconds
 * @returns {Array} Sampled telemetry data
 */
export function sampleTelemetryData(locations, intervalMs = 100) {
  if (!locations || locations.length === 0) return [];
  
  const sampled = [];
  let lastTimestamp = 0;
  
  for (const location of locations) {
    if (location.timestamp - lastTimestamp >= intervalMs) {
      sampled.push(location);
      lastTimestamp = location.timestamp;
    }
  }
  
  return sampled;
}

/**
 * Get telemetry statistics for a data range
 * @param {Array} locations - Telemetry location data
 * @param {string} metric - Metric to analyze (e.g., 'speed')
 * @returns {Object} Statistics { min, max, avg, count }
 */
export function getTelemetryStats(locations, metric) {
  if (!locations || locations.length === 0) {
    return { min: 0, max: 0, avg: 0, count: 0 };
  }
  
  const values = locations
    .map(loc => loc[metric])
    .filter(val => val !== undefined && val !== null && !isNaN(val));
  
  if (values.length === 0) {
    return { min: 0, max: 0, avg: 0, count: 0 };
  }
  
  const min = Math.min(...values);
  const max = Math.max(...values);
  const sum = values.reduce((acc, val) => acc + val, 0);
  const avg = sum / values.length;
  
  return { min, max, avg, count: values.length };
}

/**
 * Create chart data for telemetry metrics
 * @param {Array} locations - Telemetry location data
 * @param {Array} metrics - Array of metric names to include
 * @param {Object} options - Chart options { timeBase: 'elapsed'|'timestamp' }
 * @returns {Object} Chart data formatted for charting library
 */
export function createChartData(locations, metrics = ['speed'], options = {}) {
  if (!locations || locations.length === 0) return { labels: [], datasets: [] };
  
  const { timeBase = 'elapsed' } = options;
  
  // Create time labels
  const labels = locations.map(location => {
    if (timeBase === 'elapsed') {
      return location.sessionElapsedTime / 1000; // Convert to seconds
    }
    return location.timestamp;
  });
  
  // Create datasets for each metric
  const datasets = metrics.map((metric, index) => {
    const data = locations.map(location => location[metric] || 0);
    
    return {
      label: formatMetricName(metric),
      data,
      borderColor: getMetricColor(metric, index),
      backgroundColor: getMetricColor(metric, index, 0.1),
      fill: false,
      tension: 0.1
    };
  });
  
  return { labels, datasets };
}

/**
 * Format metric name for display
 * @param {string} metric - Raw metric name
 * @returns {string} Formatted metric name
 */
function formatMetricName(metric) {
  const formatMap = {
    speed: 'Speed (mph)',
    accelerometerX: 'Lateral G',
    accelerometerY: 'Longitudinal G',
    accelerometerZ: 'Vertical G',
    bearing: 'Bearing (°)',
    accuracy: 'GPS Accuracy (m)'
  };
  
  return formatMap[metric] || metric;
}

/**
 * Get color for metric charts
 * @param {string} metric - Metric name
 * @param {number} index - Fallback color index
 * @param {number} alpha - Alpha transparency (0-1)
 * @returns {string} Color string
 */
function getMetricColor(metric, index = 0, alpha = 1) {
  const colorMap = {
    speed: `rgba(59, 130, 246, ${alpha})`, // blue
    accelerometerX: `rgba(239, 68, 68, ${alpha})`, // red
    accelerometerY: `rgba(34, 197, 94, ${alpha})`, // green
    accelerometerZ: `rgba(168, 85, 247, ${alpha})`, // purple
    bearing: `rgba(251, 191, 36, ${alpha})`, // yellow
    accuracy: `rgba(156, 163, 175, ${alpha})` // gray
  };
  
  if (colorMap[metric]) return colorMap[metric];
  
  // Fallback colors
  const fallbackColors = [
    `rgba(59, 130, 246, ${alpha})`,
    `rgba(239, 68, 68, ${alpha})`,
    `rgba(34, 197, 94, ${alpha})`,
    `rgba(168, 85, 247, ${alpha})`,
    `rgba(251, 191, 36, ${alpha})`,
    `rgba(156, 163, 175, ${alpha})`
  ];
  
  return fallbackColors[index % fallbackColors.length];
} 